import React from "react";
import "./BookCoverTemplate.css";

const THEMES = ["dark", "light", "blue"];

const DEFAULT_DISCLAIMER =
  "PS: This is not the original book cover. GritinAI Hub Library curated lists of books and articles helps you stay theoretically grounded in the practical knowledge you have received.";

/**
 * BookCoverTemplate Component
 * Renders an editorial, modern tech book cover based on the GritinAI library design template.
 *
 * @param {string} title - The title of the book/article
 * @param {string} author - The author or creator name
 * @param {"dark"|"light"|"blue"|"auto"} theme - Color theme ("auto" cycles based on index)
 * @param {number} index - Position index for automatic theme rotation
 * @param {"sm"|"md"|"lg"|"responsive"} size - Visual size preset
 * @param {string} disclaimerText - Top disclaimer text
 * @param {function} onClick - Optional click handler
 * @param {string} className - Additional CSS classes
 */
const BookCoverTemplate = ({
  title = "Tech Resource",
  author = "GritinAI Library",
  theme = "auto",
  index = 0,
  size = "md",
  disclaimerText = DEFAULT_DISCLAIMER,
  onClick,
  className = "",
  style = {},
}) => {
  // Resolve theme
  let resolvedTheme = theme;
  if (theme === "auto" || !THEMES.includes(theme)) {
    resolvedTheme = THEMES[Math.abs(index) % THEMES.length];
  }

  const themeClass = `book-cover-${resolvedTheme}`;
  const sizeClass = `book-size-${size}`;

  // Clean title
  const displayTitle = title?.trim() || "Untitled Book";
  const displayAuthor = author?.trim() || "GritinAI Author";

  return (
    <div
      className={`book-cover-template ${themeClass} ${sizeClass} ${className}`}
      onClick={onClick}
      style={style}
      role={onClick ? "button" : "img"}
      aria-label={`Book cover: ${displayTitle} by ${displayAuthor}`}
    >
      {/* Top Disclaimer Note */}
      <p className="book-disclaimer">{disclaimerText}</p>

      {/* Middle Title with Accent Underline */}
      <div className="book-title-container">
        <h2 className="book-title">{displayTitle}</h2>
        <div className="book-accent-line" aria-hidden="true" />
      </div>

      {/* Bottom Author */}
      <div className="book-author-container">
        <p className="book-author">{displayAuthor}</p>
      </div>
    </div>
  );
};

export default BookCoverTemplate;
